import React from 'react';
import { cn } from '../../../lib/cn';
import type { IconKind } from '../../Icon/Icon';
import { Icon } from '../../Icon/Icon';

export interface DashboardKpiCellProps {
  label: string;
  value: string | number;
  sub?: string;
  delta?: number | null;
  deltaLabel?: string;
  color?: string;
  icon?: IconKind;
  accent?: boolean;
  /** Reference mature KPI card styling */
  variant?: 'default' | 'mature';
  className?: string;
}

export function DashboardKpiCell({
  label,
  value,
  sub,
  delta,
  deltaLabel = 'vs avg',
  color = 'var(--ink-1)',
  icon,
  accent,
  variant = 'default',
  className,
}: DashboardKpiCellProps) {
  const deltaPositive = delta != null && delta > 0;
  const deltaNegative = delta != null && delta < 0;
  const mature = variant === 'mature';

  return (
    <div
      className={cn(
        mature
          ? 'rcpt-mature-kpi rcpt-mature-fade-up'
          : 'relative flex min-h-[132px] flex-col gap-1.5 overflow-hidden rounded-[14px] border border-[var(--cream-2)] bg-[var(--cream-0)] px-[18px] py-4 shadow-sm transition-[box-shadow,transform,border-color] hover:-translate-y-0.5 hover:border-[var(--cream-2)] hover:shadow-md',
        className,
      )}
    >
      <div className={cn('flex items-center', mature ? 'gap-2' : 'gap-2')}>
        {icon ? (
          <div
            className="inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md border"
            style={{
              background: accent ? `${color}22` : 'var(--surface-mute)',
              borderColor: accent ? `${color}33` : 'var(--cream-2)',
              color: accent ? color : 'var(--ink-2)',
            }}
          >
            <Icon kind={icon} size={11} stroke={2} />
          </div>
        ) : null}
        <div
          className={cn(
            mature
              ? 'rcpt-mature-eyebrow text-[9.5px]'
              : 'font-[var(--font-mono)] text-[9.5px] font-medium uppercase tracking-[0.14em] text-[var(--ink-3)]',
          )}
        >
          {label}
        </div>
        {delta != null ? (
          <span
            className="ml-auto inline-flex items-center gap-0.5 font-[var(--font-mono)] text-[10px] font-semibold"
            style={{
              color: deltaPositive ? 'var(--ai-3)' : deltaNegative ? 'var(--terracotta)' : 'var(--ink-3)',
            }}
          >
            {deltaPositive ? '▲' : deltaNegative ? '▼' : '·'}
            {Math.abs(delta)}
            <span className="font-medium text-[var(--ink-3)]">{deltaLabel}</span>
          </span>
        ) : null}
      </div>
      <span
        className={cn(
          'font-[var(--font-display)] text-[56px] font-extrabold leading-[0.85] tracking-[-0.045em] tabular-nums',
          mature ? 'mt-2' : 'mt-2',
        )}
        style={{ color: accent ? color : 'var(--ink-0)' }}
      >
        {value}
      </span>
      {sub ? (
        <div
          className={cn(
            'mt-auto text-[11.5px] tracking-[-0.005em] text-[var(--ink-2)]',
            mature ? 'pt-2' : 'pt-2',
          )}
        >
          {sub}
        </div>
      ) : null}
    </div>
  );
}
